import { isValid, parseISO } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LoadingSpinner } from '../LoadingSpinner';
import type { NewsArticlePage } from '../Types';
import { NewsArchiveFilter, type NewsArchiveFilterState } from './NewsArchiveFilter';
import { NewsArchiveList } from './NewsArchiveList';

const PAGE_SIZE = 12; //Must match NewsHelper.PageSize on the server
const DEBOUNCE_MS = 400;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

type FilterState = Required<NewsArchiveFilterState>;

const EMPTY_FILTER: FilterState = { searchtext: '', datefrom: '', dateto: '' };

const hasFilter = ({ searchtext, datefrom, dateto }: FilterState): boolean =>
    searchtext.trim().length > 0 || datefrom.length > 0 || dateto.length > 0;

const buildUrl = (region: string, filter: FilterState, skip: number): string => {
    const params = new URLSearchParams({ region, skip: String(skip) });

    if (!hasFilter(filter)) {
        return `/api/newsarchive/action/latest?${params.toString()}`;
    }

    const query = filter.searchtext.trim();
    if (query) {
        params.set('query', query);
    }
    if (filter.datefrom) {
        params.set('from', filter.datefrom);
    }
    if (filter.dateto) {
        params.set('to', filter.dateto);
    }
    return `/api/newsarchive/action/search?${params.toString()}`;
};

const isCalendarDate = (value: string): boolean => ISO_DATE_PATTERN.test(value) && isValid(parseISO(value));

const readFilterFromUrl = (): FilterState => {
    const query = new URLSearchParams(window.location.search);
    const from = query.get('f') ?? '';
    const to = query.get('t') ?? '';
    const datefrom = isCalendarDate(from) ? from : '';
    const dateto = isCalendarDate(to) ? to : '';
    const isInverted = datefrom !== '' && dateto !== '' && datefrom > dateto;

    return {
        searchtext: query.get('q') ?? '',
        datefrom: isInverted ? '' : datefrom,
        dateto: isInverted ? '' : dateto,
    };
};

const writeFilterToUrl = ({ searchtext, datefrom, dateto }: FilterState) => {
    const url = new URL(window.location.href);
    Object.entries({ q: searchtext.trim(), f: datefrom, t: dateto }).forEach(([key, value]) => {
        if (value) {
            url.searchParams.set(key, value);
        } else {
            url.searchParams.delete(key);
        }
    });

    if (url.href !== window.location.href) {
        window.history.replaceState(window.history.state, '', url);
    }
};

export interface INewsArchivePageProps {
    region: string;
    initialItems?: NewsArticlePage[];
}

export const NewsArchivePage: React.FC<INewsArchivePageProps> = ({ region, initialItems = [] }) => {
    const [filter, setFilter] = useState<FilterState>(EMPTY_FILTER);
    const [items, setItems] = useState<NewsArticlePage[]>(initialItems);
    const [hasMore, setHasMore] = useState<boolean>(initialItems.length === PAGE_SIZE);
    const [loading, setLoading] = useState<boolean>(false);
    const [failed, setFailed] = useState<boolean>(false);
    //The sentinel is unmounted when the list is exhausted and
    //mounted again after a filter change, and the observer has to be re-attached to the new node.
    const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);

    //The request that is running or waiting for the debounce. Only one request runs at a time and
    //a filter change aborts it, so results for an old filter never end up in the list.
    const requestRef = useRef<AbortController | null>(null);
    //The server already rendered the latest news, so the initial filter isn't fetched again.
    const skipInitialFetch = useRef<boolean>(true);
    const applyFilterImmediately = useRef<boolean>(false);

    const isSearching = hasFilter(filter);

    const fetchNews = useCallback(
        async (skip: number, request: AbortController) => {
            try {
                const response = await fetch(buildUrl(region, filter, skip), { signal: request.signal });
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                const news: NewsArticlePage[] = await response.json();
                setItems((previous) => (skip === 0 ? news : [...previous, ...news]));
                setHasMore(news.length === PAGE_SIZE);
            } catch (error) {
                if (error.name !== 'AbortError') {
                    setFailed(true);
                    setHasMore(false);
                    if (skip === 0) {
                        setItems([]);
                    }
                }
            } finally {
                if (requestRef.current === request) {
                    requestRef.current = null;
                    setLoading(false);
                }
            }
        },
        [region, filter],
    );

    const handleFilterUpdate = useCallback((data: NewsArchiveFilterState) => {
        setFilter((previous) => ({ ...previous, ...data }));
    }, []);

    //The server renders the latest news regardless of the query string, so a filter from the url
    //is applied after hydration rather than during the first render.
    useEffect(() => {
        const urlFilter = readFilterFromUrl();
        if (hasFilter(urlFilter)) {
            applyFilterImmediately.current = true;
            setFilter(urlFilter);
        }
    }, []);

    useEffect(() => {
        if (skipInitialFetch.current) {
            skipInitialFetch.current = false;
            return;
        }

        //Start over from the first page: abort the request for the old filter, block "load more" until
        //the new result has arrived, and page again even if the old filter had been exhausted or failed.
        requestRef.current?.abort();
        const request = new AbortController();
        requestRef.current = request;
        setLoading(true);
        setHasMore(true);
        setFailed(false);

        const delay = applyFilterImmediately.current ? 0 : DEBOUNCE_MS;
        applyFilterImmediately.current = false;

        const timeoutId = setTimeout(() => {
            writeFilterToUrl(filter);
            fetchNews(0, request);
        }, delay);

        return () => clearTimeout(timeoutId);
    }, [filter, fetchNews]);

    const loadMore = useCallback(() => {
        if (requestRef.current) {
            return;
        }

        const request = new AbortController();
        requestRef.current = request;
        setLoading(true);
        fetchNews(items.length, request);
    }, [fetchNews, items.length]);

    useEffect(() => {
        if (!sentinel) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMore();
                }
            },
            { rootMargin: '300px' },
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [sentinel, loadMore]);

    return (
        <div>
            <div className="row mb-4">
                <NewsArchiveFilter {...filter} onUpdate={handleFilterUpdate} />
            </div>
            <div className="row">
                <div className="col-12">
                    {items.length === 0 && !loading && !failed ? (
                        <p className="no-result news-archive__no-items">Det finns inga resultat att visa</p>
                    ) : (
                        <NewsArchiveList items={items} />
                    )}
                    {failed && (
                        <p className="no-result news-archive__no-items" role="alert">
                            Det gick inte att hämta nyheter
                        </p>
                    )}
                    {loading && (
                        <div className="news-archive__loader" role="alert" aria-busy="true">
                            <LoadingSpinner />
                            <span className="visually-hidden">Laddar nyheter</span>
                        </div>
                    )}
                    {items.length > 0 && hasMore && <div ref={setSentinel} />}
                    {!hasMore && !failed && !isSearching && items.length > 0 && (
                        <p className="news-archive__end-message">Använd sökfunktionen för att hitta äldre nyheter</p>
                    )}
                </div>
            </div>
        </div>
    );
};
